import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Home, Users, Building, Building2, Landmark, Tag, ListChecks, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { UserMenu } from "./UserMenu";
import { MobileNav } from "./MobileNav";
import { cn } from "@/lib/utils";

export const NAV_ITEMS = [
  { to: "/", label: "Accueil", icon: Home, end: true, id: "home" },
  { to: "/personnes", label: "Personnes", icon: Users, id: "persons" },
  { to: "/entreprises", label: "Entreprises", icon: Building2, id: "organizations" },
  { to: "/locataires", label: "Locataires & Domiciliés", icon: Building, id: "tenants" },
  { to: "/clubs", label: "Clubs", icon: UsersRound, id: "clubs" },
  { to: "/commissions", label: "Commissions", icon: Landmark, id: "commissions" },
  { to: "/expertises", label: "Expertises", icon: Tag, id: "expertises" },
  { to: "/listes", label: "Listes", icon: ListChecks, id: "lists" },
];

export const Logo = ({ className }) => (
  <img src="/logo-lamelee.svg" alt="La Mêlée" className={cn("h-9 w-auto", className)} />
);

export function AppLayout() {
  const { isAuthed, loading, config } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-border/70 bg-white/85 backdrop-blur-xl">
        <div className="lm-container flex h-16 items-center gap-4">
          <Link to="/" className="flex shrink-0 items-center gap-2" data-testid="nav-logo-link">
            <Logo className="h-12" />
            {config?.data_source === "mock" && (
              <span data-testid="demo-mode-badge" className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-orange-700">
                Démo
              </span>
            )}
          </Link>
          <nav className="ml-2 hidden items-center gap-0.5 xl:flex">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.id}-link`}
                className={({ isActive }) =>
                  cn(
                    "rounded-full px-3 py-2 text-sm font-semibold transition-colors",
                    isActive ? "bg-accent text-primary" : "text-stone-600 hover:bg-stone-100 hover:text-stone-900",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {!loading && (isAuthed ? <UserMenu /> : (
              <Button asChild className="rounded-full px-5 font-semibold" data-testid="nav-login-button">
                <Link to={`/connexion?next=${encodeURIComponent(pathname)}`}>Se connecter</Link>
              </Button>
            ))}
            <MobileNav />
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-border/70 bg-white/60">
        <div className="lm-container flex flex-col gap-2 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <span>Annuaire interne La Mêlée — données issues de Notion</span>
          {config?.data_source === "mock" && <span data-testid="footer-demo-notice">Mode démonstration : données fictives</span>}
        </div>
      </footer>
    </div>
  );
}
